import { useState, type ReactNode } from 'react';
import { MessageSquareIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import appPageCopy from '@/i18n/ns/app-page';
import panelsCopy from '@/i18n/ns/artifact-panels';
import type { SavedAppDisplay } from '../../../src/artifacts/saved-view-contract.mjs';
import type { ArtifactCanvasTarget, DraftCanvasTarget } from '../artifacts/routes';
import { useCanvasItemName, useCanvasKindLabel, useCanvasTitle } from '../artifacts/outlet';
import { CanvasHeaderTools } from '../canvas-footer';
import { useOptionalWorkspace } from '../context';
import { AppCannotOpen, ChangeWithChatContext } from './app-opening';
import { isAppPageMode } from './app-page-mode';
import { AppSurface, type AppSurfaceProps } from './app-surface';
import { useEditingAppSelection } from './editing-selection';
import { openAppChat } from './open-app-chat';

/**
 * What the host binds to ONE selected saved App: the SDK lifetime and frame the App runs in, the data
 * projections, and the handlers (already bound to this selection). A portion that cannot be supplied is
 * null and is shown as unavailable. The host (APP's saved-app-host) registers its hook once with
 * `registerSelectedAppHost`; nothing here fetches or invents a value.
 */
export type SelectedAppHost = {
  lifetime: AppSurfaceProps['lifetime'];
  appFrame: ReactNode;
  data: AppSurfaceProps['data'];
  history: AppSurfaceProps['history'];
  versions: AppSurfaceProps['versions'];
  completedVersions?: ReactNode;
  versionsControls?: ReactNode;
  /** The opened document is a Deck (from its own issued document): presented as a deck, not as an App. */
  deck?: boolean;
  on: NonNullable<AppSurfaceProps['page']>['on'];
  hostOverlays?: ReactNode;
  inspectOn?: boolean;
  initialTab?: AppSurfaceProps['initialTab'];
  revealToken?: AppSurfaceProps['revealToken'];
  onTabChange?: AppSurfaceProps['onTabChange'];
};
export type SelectedAppHostInput = {
  onSharingChanged?: () => void;
  display: SavedAppDisplay | null;
  target: ArtifactCanvasTarget | DraftCanvasTarget;
  companyId: string;
  actorId: string;
};
export type UseSelectedAppHost = (input: SelectedAppHostInput) => SelectedAppHost | null;

const noHost: UseSelectedAppHost = () => null;
let useHost: UseSelectedAppHost = noHost;
/** Called once by the root registration, before the first render of a saved App. */
export function registerSelectedAppHost(hook: UseSelectedAppHost) {
  useHost = hook;
}
export function resetSelectedAppHostForTests() {
  useHost = noHost;
}

/**
 * Change with Chat, on the App's own page and for the person who made it: the chat it was made in reopens with the
 * App beside it, as one link (that chat and this App's canvas), so Back returns to the page.
 */
function useChangeWithChat(target: ArtifactCanvasTarget | DraftCanvasTarget) {
  const ws = useOptionalWorkspace();
  const copy = useNamespaceCopy(appPageCopy);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    if (target.kind !== 'artifact') return;
    setBusy(true);
    try {
      await openAppChat({
        artifactId: target.id,
        versionId: target.versionId,
        revisionId: target.revisionId,
      });
    } catch {
      ws?.notify(copy.changeInChatUnavailable, null, 'error');
    } finally {
      setBusy(false);
    }
  };
  return { open: () => void open(), busy };
}

function ChangeWithChat({ open, busy }: { open: () => void; busy: boolean }) {
  const copy = useNamespaceCopy(appPageCopy);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      data-testid="app-change-with-chat"
      aria-label={copy.changeInChat}
      title={copy.changeInChat}
      disabled={busy}
      onClick={open}
    >
      <MessageSquareIcon aria-hidden="true" />
      <span className="ws-app-change-label">{copy.changeInChat}</span>
    </Button>
  );
}

/** The facts in the saved display, plus the host's selection-bound content. */
export function AppSavedDisplay(props: SelectedAppHostInput) {
  useEditingAppSelection(
    `${props.target.kind}:${props.target.id}`,
    props.target.kind === 'artifact'
      ? {
          artifactId: props.target.id,
          versionId: props.target.versionId,
          revisionId: props.target.revisionId,
        }
      : undefined,
  );
  // The canvas header names its kind "App", not the generic "Artifact".
  const panels = useNamespaceCopy(panelsCopy);
  const host = useHost(props);
  useCanvasKindLabel(host?.deck ? panels.kindDeck : panels.kindApp);
  useCanvasTitle(props.display?.app.name ?? null);
  useCanvasItemName(props.display?.app.name ?? null);
  const locale = useChatInterfaceLocale();
  const ws = useOptionalWorkspace();
  // The App's own page (opened from Apps): one top bar holds its actions; nothing of the chat around it.
  const ownPage = Boolean(ws && isAppPageMode(ws.view, ws.canvas));
  const change = useChangeWithChat(props.target);
  if (!host) return <AppCannotOpen artifactId={props.target.id} name={props.display?.app.name} />;
  const { display } = props;
  const failure = display?.failure && {
    ...display.failure,
    // PUB gives a canonical instant; it is formatted once, here, for the viewer's locale.
    time: new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(display.failure.time),
    ),
  };
  // Anyone who can edit it (its owner or an editor, not a user) changes it in chat: from the top bar,
  // and from its cannot-open state.
  const changes =
    ownPage &&
    display?.rights.role !== 'user' &&
    Boolean(display) &&
    props.target.kind === 'artifact';
  return (
    <ChangeWithChatContext.Provider value={changes ? change : null}>
      {changes && (
        <CanvasHeaderTools>
          <ChangeWithChat {...change} />
        </CanvasHeaderTools>
      )}
      <AppSurface
        actionsInBar={ownPage}
        page={
          display
            ? {
                app: display.app,
                savedVersion: display.savedVersion,
                rights: display.rights,
                failure: failure ?? null,
                deck: host.deck === true,
                on: host.on,
              }
            : null
        }
        appFrame={host.appFrame}
        data={host.data}
        history={host.history}
        versions={host.versions}
        completedVersions={host.completedVersions}
        versionsControls={host.versionsControls}
        lifetime={host.lifetime}
        hostOverlays={host.hostOverlays}
        inspectOn={host.inspectOn}
        initialTab={host.initialTab}
        revealToken={host.revealToken}
        onTabChange={host.onTabChange}
      />
    </ChangeWithChatContext.Provider>
  );
}
